import type { Club } from './types'

/** Every club with no readable availability shares one muted colour, so the map
 * reads at a glance as "these you have to phone". */
export const MUTED = '#94a3b8'

/**
 * The padel registry for Madeira + Porto Santo.
 *
 * Every id, coordinate and price below was read from the club's own booking
 * platform or geocoded from its published address - nothing here is estimated.
 * Where a platform does not publish a field (court surface, coordinates for the
 * government portal's venues) it is left out rather than guessed.
 *
 * Adding a club: find its platform identifiers, add the `ref`, and list its
 * courts with the ids that platform's availability response uses as join keys.
 */
export const CLUBS: Club[] = [
  // ---------------------------------------------------------------- Simplifica
  {
    id: 'quinta-magnolia',
    name: 'Quinta Magnólia',
    shortName: 'Q. Magnólia',
    municipality: 'Funchal',
    region: 'Funchal',
    // Centroid of the three padel pitches mapped in OSM inside the park.
    lat: 32.64452,
    lon: -16.92418,
    source: 'simplifica',
    ref: { kind: 'simplifica', serviceId: 102, idProcess: 32 },
    bookingUrl: 'https://simplifica.madeira.gov.pt/services/3-53-102',
    color: '#2dd4a7',
    live: true,
    phone: '+351 291 145 788',
    // Portaria n.º 143/2017 as republished by Portaria n.º 505/2019, Anexo I.
    // The NET column, not the "c/ IVA 22%" one: Artigo 15.º n.º 2 exempts
    // "utilizadores diretos" from IVA, and n.º 3 defines those as "uma pessoa
    // singular ou um grupo de pessoas singulares" - i.e. anyone booking a court
    // to play on it. Quoting the VAT-inclusive figure overstated every price
    // by 22%.
    pricing: { weekday: 8, evening: 10, weekend: 10, eveningFrom: '18:00' },
    courts: [
      { id: '127', name: 'Campo de Padel 1' },
      { id: '129', name: 'Campo de Padel 2' },
      { id: '130', name: 'Campo de Padel 3' },
    ],
    note: 'Booking requires a Simplifica account. Court 1 has shown no availability for weeks - likely blocked rather than booked.',
    notePt:
      'A reserva exige conta Simplifica. O campo 1 não mostra disponibilidade há semanas - provavelmente está bloqueado, não reservado.',
  },
  {
    id: 'lobos-park',
    name: 'MacPadel',
    shortName: 'MacPadel',
    municipality: 'Câmara de Lobos',
    region: 'West',
    // The two padel pitches inside the Parque Desportivo da Cidade, per OSM.
    lat: 32.65119,
    lon: -16.97922,
    source: 'playtomic',
    ref: { kind: 'playtomic', tenantId: 'd159dfbc-3792-4279-9ec0-2dd1cd8ec6cf' },
    bookingUrl: 'https://playtomic.com/clubs/sublime-comporta-terracota',
    color: '#60a5fa',
    live: true,
    courts: [
      { id: 'f6115d56-9a6a-4808-93b5-dde0146d70c8', name: 'Campo Norte', surface: 'outdoor' },
      { id: '51f1e85c-da83-4100-888d-ffb6dee0c34c', name: 'Campo Sul', surface: 'outdoor' },
    ],
    note: 'Formerly the free municipal Lobos Park courts. A private concession took over on 1 September 2026: bookings moved from Simplifica to Playtomic and the courts are no longer free. Same location, Avenida Nova Cidade.',
    notePt:
      'Antigos campos municipais gratuitos do Lobos Park. Uma concessão privada assumiu a gestão a 1 de setembro de 2026: as reservas passaram do Simplifica para o Playtomic e os campos deixaram de ser gratuitos. Mesmo local, Avenida Nova Cidade.',
  },

  // ----------------------------------------------------------------- Playtomic
  {
    id: 'centro-padel-lazer',
    name: 'Centro de Padel e Lazer',
    shortName: 'Padel e Lazer',
    municipality: 'Funchal',
    region: 'Funchal',
    lat: 32.6535383,
    lon: -16.9085253,
    source: 'playtomic',
    ref: { kind: 'playtomic', tenantId: 'bde4f48e-3176-42e5-aa1e-ee383be8d564' },
    bookingUrl: 'https://playtomic.com/clubs/centro-de-padel-e-lazer',
    color: '#a78bfa',
    live: true,
    phone: '+351 291 647 790',
    horizonDays: 10,
    // This tenant has SHOW_AVAILABILITY_GRID false, so its Playtomic page
    // renders no date picker and a ?date= link would promise nothing.
    deepLink: false,
    courts: [
      { id: '369851fc-0ff9-43b1-a745-b5ddd49be667', name: 'Campo Decathlon', surface: 'indoor' },
      { id: '464e8fa6-2048-417a-860b-c3d2d6038d74', name: 'Campo Barbeito', surface: 'indoor' },
      { id: '483d0a37-21ed-467b-b872-1dd451b96abf', name: 'Campo Coral', surface: 'indoor' },
    ],
    note: 'R. das Hortas 101, Funchal. Also listed on Aircourts, so availability can differ between the two.',
    notePt:
      'R. das Hortas 101, Funchal. Também listado no Aircourts, pelo que a disponibilidade pode diferir entre os dois.',
  },
  {
    id: 'centro-desportivo-madeira',
    name: 'Centro Desportivo da Madeira',
    shortName: 'C. Desportivo',
    municipality: 'Ribeira Brava',
    region: 'West',
    lat: 32.690679,
    lon: -17.054473,
    source: 'playtomic',
    ref: { kind: 'playtomic', tenantId: '893ee9cb-3f79-4fae-878e-af0fc03ea6af' },
    bookingUrl: 'https://playtomic.com/clubs/centro-desportivo-da-madeira',
    color: '#f472b6',
    live: true,
    phone: '+351 291 950 120',
    horizonDays: 14,
    courts: [
      { id: '60dd9d53-caae-4373-a91c-7b903854967c', name: 'Campo Padel 1', surface: 'outdoor' },
      { id: 'a4f96d33-8c3a-4f43-80c4-fa0bb44df41b', name: 'Campo Padel 2', surface: 'outdoor' },
    ],
    note: 'Multi-sport venue - only the two padel courts are listed here. Cheapest padel on the island.',
    notePt:
      'Complexo multidesportivo - apenas os dois campos de padel aparecem aqui. O padel mais barato da ilha.',
  },
  {
    id: 'calheta',
    name: 'Padel Porto de Recreio da Calheta',
    shortName: 'Calheta',
    municipality: 'Calheta',
    region: 'West',
    lat: 32.717711,
    lon: -17.1705684,
    source: 'playtomic',
    ref: { kind: 'playtomic', tenantId: '110af1e1-bd97-4f6a-8e7d-779b5b9c73ee' },
    bookingUrl: 'https://playtomic.com/clubs/padel-porto-de-recreio-da-calheta',
    color: '#38bdf8',
    live: true,
    phone: '+351 916 598 064',
    horizonDays: 20,
    courts: [
      { id: 'aa07815c-63d3-4c3d-8ed3-242dce0e6450', name: 'Campo 1', surface: 'outdoor' },
      { id: 'dcebbdd2-be0a-4188-9e56-9a33c67af4d7', name: 'Campo 2', surface: 'outdoor' },
    ],
    note: 'At the Calheta marina.',
    notePt:
      'Na marina da Calheta.',
  },
  {
    id: 'vila-baleira',
    name: 'Padel Vila Baleira',
    shortName: 'Vila Baleira',
    municipality: 'Porto Santo',
    region: 'Porto Santo',
    lat: 33.03733301,
    lon: -16.36274764,
    source: 'playtomic',
    ref: { kind: 'playtomic', tenantId: 'b92bb4e0-7cff-4c57-ac66-f52e2c4a79a9' },
    bookingUrl: 'https://playtomic.com/clubs/padel-vila-baleira',
    color: '#fb923c',
    live: true,
    phone: '+351 291 980 800',
    horizonDays: 14,
    courts: [
      { id: '5711e678-7200-43cc-ac84-4b16d34c9764', name: 'Campo 1', surface: 'outdoor' },
      { id: 'd2cd5e84-0ba3-480d-a1e8-96e13aabab6e', name: 'Campo 2', surface: 'outdoor' },
      { id: 'd2267e2a-a545-4885-b6f5-37a61f24bea6', name: 'Campo 3', surface: 'outdoor' },
      { id: '3de874b8-5835-4b9b-8f6d-f2999e059fe3', name: 'Campo 4', surface: 'outdoor' },
    ],
    note: 'Porto Santo - attached to the Vila Baleira Thalassa hotel.',
    notePt:
      'Porto Santo - junto ao hotel Vila Baleira Thalassa.',
  },
  {
    id: 'porto-santo-racket',
    name: 'Porto Santo Racket Center',
    shortName: 'PS Racket',
    municipality: 'Porto Santo',
    region: 'Porto Santo',
    // Centroid of the four padel pitches at the Campo de Baixo complex, per OSM.
    lat: 33.04664,
    lon: -16.35853,
    source: 'playtomic',
    ref: { kind: 'playtomic', tenantId: '05ab0615-ffb5-47ef-a948-283fdf0d38ee' },
    bookingUrl: 'https://playtomic.com/clubs/porto-santo-racket-center',
    color: '#4ade80',
    live: true,
    phone: '+351 291 982 170',
    horizonDays: 7,
    courts: [
      { id: '6b579e2d-cd26-46bb-9d01-4123092e1546', name: 'Padel 1', surface: 'outdoor' },
      { id: '137bd7ae-00db-4df5-9c95-722b192cad2b', name: 'Padel 2', surface: 'outdoor' },
      { id: 'fbc60525-8d85-4a24-b54c-0edaade663b2', name: 'Padel 3', surface: 'outdoor' },
      { id: '94af0875-2d67-454e-b477-d7f7dd2a2f5c', name: 'Padel 4', surface: 'outdoor' },
    ],
    note: 'Porto Santo. Also has tennis courts - only padel is shown.',
    notePt:
      'Porto Santo. Também tem campos de ténis - só o padel é mostrado.',
  },

  {
    id: 'nexo-padel',
    name: 'Nexo Padel Club',
    shortName: 'Nexo',
    municipality: 'Câmara de Lobos',
    region: 'West',
    lat: 32.67070374,
    lon: -16.97744146,
    source: 'playtomic',
    ref: { kind: 'playtomic', tenantId: '73000cd4-9369-456a-a09c-e7f3778b18dd' },
    bookingUrl: 'https://playtomic.com/clubs/nexo-padel-club',
    color: '#22d3ee',
    live: true,
    horizonDays: 14,
    courts: [
      { id: 'ba2dada7-6941-40c8-8468-00592a30f8ae', name: 'Padel 1', surface: 'outdoor' },
      { id: '2208132b-a616-47f1-89f5-4337b240ae01', name: 'Padel 2', surface: 'outdoor' },
    ],
  },

  // --------------------------------------------------------------------- Field
  {
    id: 'jardim-panoramico',
    name: 'Padel Jardim Panorâmico do Lido',
    shortName: 'J. Panorâmico',
    municipality: 'Funchal',
    region: 'Funchal',
    lat: 32.636545,
    lon: -16.9386197,
    source: 'field',
    ref: { kind: 'field', clubId: 'f2JIwIOBXaSa95zlDdXI' },
    // Singular /club/ - the SPA has no /clubs/:id route and silently lands on
    // its homepage instead, which is where every booking click used to go.
    bookingUrl: 'https://getfield.app/club/f2JIwIOBXaSa95zlDdXI',
    color: '#facc15',
    live: true,
    phone: '+351 291 106 550',
    courts: [
      { id: 'KDbEOwoP1Lxs1gXG0ady', name: 'Campo 1', surface: 'outdoor' },
      { id: 'tdXqrFUeHI6833oRBl9X', name: 'Campo 2', surface: 'outdoor' },
      { id: 'qKCnKkZCYumtbKTtnUzu', name: 'Campo WPT', surface: 'outdoor' },
    ],
    note: 'Lido area, Funchal. Campo WPT is the premium court and costs more. Shown 08:00-22:30; weekdays may open an hour earlier than listed here.',
    notePt:
      'Zona do Lido, Funchal. O Campo WPT é o campo premium e custa mais. Mostrado das 08:00 às 22:30; nos dias úteis pode abrir uma hora mais cedo.',
  },

  // ----------------------------------------------------------------- TieSports
  {
    id: 'padel-centro-canico',
    name: 'Padel Centro Caniço',
    shortName: 'Caniço',
    municipality: 'Caniço',
    region: 'East',
    lat: 32.64965,
    lon: -16.8422,
    source: 'tieplayer',
    ref: { kind: 'tiesports', slug: 'PadelCentroCanico' },
    bookingUrl: 'https://clubs.tiesports.com/en/madeira/PadelCentroCanico',
    color: '#f59e0b',
    live: true,
    horizonDays: 15,
    courts: [
      { id: '8445', name: 'Campo Alberto Oculista', surface: 'outdoor' },
      { id: '8446', name: 'Campo Novo Super', surface: 'outdoor' },
    ],
    note: 'Open 09:00-22:00 daily. Their host sits behind a bot challenge, so live availability here can drop out - the site itself is always accurate.',
    notePt:
      'Aberto todos os dias das 09:00 às 22:00. O servidor deles tem proteção anti-robôs, por isso a disponibilidade ao vivo pode falhar - o site do clube está sempre correto.',
  },

  // ---------------------------------------------------------------- MatchPoint
  {
    id: 'play-padel-madeira',
    name: 'Play Padel Madeira',
    shortName: 'Play Padel',
    municipality: 'Funchal',
    region: 'Funchal',
    // Inside the Clube de Ténis do Funchal grounds on Rua Dr. Pita. Listings
    // under "CTF Funchal Tennis & Padel" are this same site, not a second one.
    lat: 32.6454975,
    lon: -16.9270739,
    source: 'matchpoint',
    ref: { kind: 'matchpoint', host: 'playpadelmadeira-pt.matchpoint.com.es', idCuadro: 4 },
    bookingUrl: 'https://playpadelmadeira-pt.matchpoint.com.es/Booking/Grid.aspx',
    color: '#818cf8',
    live: true,
    horizonDays: 30,
    courts: [
      { id: '13', name: 'Grupo Sousa', surface: 'outdoor' },
      { id: '14', name: 'Hospital da Luz', surface: 'outdoor' },
      { id: '15', name: 'Coral Puro Malte', surface: 'outdoor' },
    ],
    note: 'Open 08:00-22:30 daily. Their system does not publish prices to non-members, so slots here show no cost.',
    notePt:
      'Aberto todos os dias das 08:00 às 22:30. O sistema deles não publica preços a não sócios, por isso os horários aqui não mostram custo.',
  },

  // -------------------------------------------------------------- No live feed
  {
    id: 'ludens-machico',
    name: 'Campo de Padel do Ludens',
    shortName: 'Ludens',
    municipality: 'Machico',
    region: 'East',
    lat: 32.7181223,
    lon: -16.7637,
    source: 'manual',
    ref: { kind: 'manual' },
    bookingUrl: 'https://www.ludensmachico.pt/reservas/',
    color: MUTED,
    live: false,
    phone: '+351 925 783 480',
    courts: [{ id: 'ludens-1', name: 'Campo de Padel' }],
    note: 'One court, booked through the club\'s own site (members only, so availability cannot be read). €10 per 1h30 before 17:00, €16 after.',
    notePt:
      'Um campo, reservado no site do próprio clube (só sócios, por isso a disponibilidade não pode ser lida). 10 EUR por 1h30 antes das 17:00, 16 EUR depois.',
  },
  {
    id: 'quinta-do-padel',
    name: 'Clube Quinta do Padel',
    shortName: 'Quinta do Padel',
    municipality: 'Funchal',
    region: 'Funchal',
    lat: 32.6575913,
    lon: -16.9370137,
    source: 'manual',
    ref: { kind: 'manual' },
    bookingUrl: 'https://quintadopadel.matchpoint.com.es/',
    color: MUTED,
    live: false,
    courts: [{ id: 'qdp-1', name: 'Courts 1-5' }],
    note: 'Five covered courts, the largest club in Funchal. Its booking grid is behind a member login, so free slots cannot be read - around €6.35 per player, about €25 for the court, for 60 min.',
    notePt:
      'Cinco campos cobertos, o maior clube do Funchal. A grelha de reservas exige login de sócio, por isso não é possível ler os horários livres - cerca de 6,35 EUR por jogador, cerca de 25 EUR pelo campo, para 60 min.',
  },
  {
    id: 'agua-de-pena',
    name: 'Parque Desportivo da Água de Pena',
    shortName: 'Água de Pena',
    municipality: 'Machico',
    region: 'East',
    lat: 32.70615,
    lon: -16.76428,
    source: 'manual',
    ref: { kind: 'manual' },
    bookingUrl: 'https://mmachico.wixsite.com/pdap',
    color: MUTED,
    live: false,
    phone: '+351 291 961 251',
    courts: [
      { id: 'adp-1', name: 'Campo 1', surface: 'outdoor' },
      { id: 'adp-2', name: 'Campo 2', surface: 'outdoor' },
      { id: 'adp-3', name: 'Campo 3', surface: 'outdoor' },
    ],
    note: 'Three artificial-turf courts. Open 10:00-22:00 weekdays, 10:00-19:00 weekends and holidays. Bookings by phone only.',
    notePt:
      'Três campos de relva sintética. Aberto das 10:00 às 22:00 nos dias úteis e das 10:00 às 19:00 aos fins de semana e feriados. Reservas apenas por telefone.',
  },
  {
    id: 'sao-vicente',
    name: 'Padel da Frente Mar de São Vicente',
    shortName: 'São Vicente',
    municipality: 'São Vicente',
    region: 'North',
    lat: 32.80679,
    lon: -17.04686,
    source: 'manual',
    ref: { kind: 'manual' },
    bookingUrl: 'https://www.cm-saovicente.pt/',
    color: MUTED,
    live: false,
    courts: [
      { id: 'sv-1', name: 'Campo 1', surface: 'outdoor' },
      { id: 'sv-2', name: 'Campo 2', surface: 'outdoor' },
    ],
    note: 'Two municipal courts on the north-coast seafront, opened October 2025. A December 2025 storm shattered their glass - check with the câmara that they are back in use before travelling.',
    notePt:
      'Dois campos municipais na frente mar da costa norte, inaugurados em outubro de 2025. Uma tempestade em dezembro de 2025 partiu os vidros - confirme com a câmara que já estão em uso antes de se deslocar.',
  },
  {
    id: 'jardim-do-mar',
    name: 'Padel Jardim do Mar',
    shortName: 'Jardim do Mar',
    municipality: 'Calheta',
    region: 'West',
    lat: 32.73742,
    lon: -17.20917,
    source: 'manual',
    ref: { kind: 'manual' },
    bookingUrl: 'https://www.cm-calheta.pt/',
    color: MUTED,
    live: false,
    courts: [{ id: 'jdm-1', name: 'Padel court' }],
    note: 'A single court in Jardim do Mar village. No online booking found - ask locally.',
    notePt:
      'Um único campo na aldeia do Jardim do Mar. Não foi encontrada reserva online - pergunte no local.',
  },
  {
    id: 'royal-savoy',
    name: 'Royal Savoy',
    shortName: 'Royal Savoy',
    municipality: 'Funchal',
    region: 'Funchal',
    lat: 32.6420662,
    lon: -16.9204799,
    source: 'manual',
    ref: { kind: 'manual' },
    bookingUrl: 'https://www.savoysignature.com/royalsavoyhotel/en/facilities/',
    color: MUTED,
    live: false,
    phone: '+351 291 213 500',
    courts: [{ id: 'rs-1', name: 'Padel court' }],
    note: 'Hotel court, open to non-guests: €20 an hour, 08:00-22:00 daily, booked in advance at reception or by phone. Free parking for visitors when available.',
    notePt:
      'Campo de hotel, aberto a não hóspedes: 20 EUR por hora, das 08:00 às 22:00 todos os dias, com reserva prévia na receção ou por telefone. Estacionamento gratuito para visitantes quando disponível.',
  },
  {
    id: 'views-baia',
    name: 'The Views Baía',
    shortName: 'Views Baía',
    municipality: 'Funchal',
    region: 'Funchal',
    lat: 32.6491435,
    lon: -16.919511,
    source: 'manual',
    ref: { kind: 'manual' },
    bookingUrl: 'https://www.theviewshotels.pt/baia/servicos/',
    color: MUTED,
    live: false,
    courts: [{ id: 'vb-1', name: 'Padel court' }],
    note: 'Hotel court. Nothing published about non-guest access - ring reception before counting on it.',
    notePt:
      'Campo de hotel. Nada publicado sobre acesso a não hóspedes - ligue para a receção antes de contar com ele.',
  },
  {
    id: 'pestana-porto-santo',
    name: 'Pestana Porto Santo',
    shortName: 'Pestana PS',
    municipality: 'Porto Santo',
    region: 'Porto Santo',
    lat: 33.04337,
    lon: -16.3577,
    source: 'manual',
    ref: { kind: 'manual' },
    bookingUrl: 'https://www.pestana.com/pt/hotel/pestana-porto-santo',
    color: MUTED,
    live: false,
    courts: [{ id: 'pps-1', name: 'Padel court' }],
    note: 'Resort court, effectively guests only - rackets and balls are issued at reception. Shared by the Pestana Porto Santo, Colombos and Dunas properties.',
    notePt:
      'Campo de resort, na prática só para hóspedes - raquetes e bolas são fornecidas na receção. Partilhado pelas unidades Pestana Porto Santo, Colombos e Dunas.',
  },
]

export const CLUB_BY_ID = new Map(CLUBS.map((club) => [club.id, club]))
